import type { IApiKey } from '@experiences/interfaces';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import {
    Spacing,
    Typography,
} from '@uipath/apollo-core';
import React, {
    useEffect,
    useState,
} from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import {
    consumableCodeToApiKeyIdentifier,
    notificationType,
} from '../../../../common/constants/Constant';
import { useUiSnackBar } from '../../../../common/hooks/useUiSnackBar';
import { isAdminSelector } from '../../../../store/selectors';
import { useRegenerateMlKey } from '../../helperComponents/UiConsumableCard/useRegenerateMlKey';
import { visuallyHiddenStyle } from '../../helperComponents/visuallyHiddenStyle';
import { useCanManageLicensingData } from '../../useCanManageLicensingData';

// Fixed-length mask, independent of the real key's length.
const MASKED_API_KEY = '•'.repeat(32);

const useStyles = makeStyles(theme =>
    createStyles({
        sectionLabel: {
            display: 'flex',
            alignItems: 'center',
            gap: Spacing.SpacingMicro,
            fontSize: Typography.fontSizeSBold.fontSize,
            fontWeight: Typography.fontSizeSBold.fontWeight,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        row: {
            display: 'flex',
            alignItems: 'center',
            gap: Spacing.SpacingS,
            marginTop: Spacing.SpacingMicro,
        },
        keyText: {
            // Containing block for the absolute visuallyHidden span; without it the span escapes the
            // page's scroll container and adds phantom scrollable height.
            position: 'relative',
            flex: 1,
            minWidth: 0,
            fontFamily: 'monospace',
            fontSize: Typography.fontSizeS.fontSize,
            color: theme.palette.semantic.colorForeground,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        actions: {
            display: 'flex',
            alignItems: 'center',
            gap: Spacing.SpacingMicro,
            flex: 'none',
        },
        visuallyHidden: visuallyHiddenStyle,
    }),
);

export const UiApiKeySecretV2: React.FC<{ consumableCode: string; apiKey?: IApiKey; tenantId?: string }> = ({
    consumableCode, apiKey, tenantId,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const [ showKey, setShowKey ] = useState(false);
    const { createNotification } = useUiSnackBar();
    const {
        loading, regenerate,
    } = useRegenerateMlKey(tenantId);

    useEffect(() => {
        setShowKey(false);
    }, [ apiKey?.value ]);

    const isAdmin = useSelector(isAdminSelector);
    const canManageLicensingData = useCanManageLicensingData();
    // Both reveal and copy are gated the same way regenerate already was — a non-admin/non-manager
    // must not be able to get the raw secret at all, whether on-screen or via clipboard.
    const canAccessSecret = isAdmin || canManageLicensingData;

    return (
        <div data-cy={`consumables-v2-api-key-${consumableCode.toLowerCase()}`}>
            <div className={classes.sectionLabel}>
                <LockOutlinedIcon sx={{ fontSize: Typography.fontSizeS.fontSize }} />
                {translate({ id: 'CLIENT_CONSUMABLE_V2_API_KEY_SECRET_LABEL' })}
            </div>
            <div className={classes.row}>
                {loading ? <CircularProgress size={16} /> : (
                    <Tooltip title={apiKey?.value && showKey && canAccessSecret ? apiKey.value : ''}>
                        <span
                            className={classes.keyText}
                            data-cy={`consumables-v2-api-key-value-${consumableCode.toLowerCase()}`}>
                            {apiKey?.value
                                ? (showKey && canAccessSecret ? apiKey.value : (
                                    <>
                                        <span aria-hidden="true">
                                            {MASKED_API_KEY}
                                        </span>
                                        <span className={classes.visuallyHidden}>
                                            {translate({ id: 'CLIENT_API_KEY_HIDDEN' })}
                                        </span>
                                    </>
                                ))
                                : translate({ id: 'CLIENT_NOT_GENERATED_YET' })}
                        </span>
                    </Tooltip>
                )}
                <div className={classes.actions}>
                    {apiKey?.value && canAccessSecret && (
                        <Tooltip title={translate({ id: showKey ? 'CLIENT_HIDE_API_KEY' : 'CLIENT_SHOW_API_KEY' })}>
                            <IconButton
                                size="small"
                                aria-label={translate({ id: showKey ? 'CLIENT_HIDE_API_KEY' : 'CLIENT_SHOW_API_KEY' })}
                                aria-pressed={showKey}
                                data-cy={`consumables-v2-toggle-reveal-${consumableCode.toLowerCase()}`}
                                onClick={() => setShowKey(previous => !previous)}
                            >
                                {showKey
                                    ? <VisibilityOffOutlinedIcon
                                        color="primary"
                                        fontSize="small" />
                                    : <VisibilityOutlinedIcon
                                        color="primary"
                                        fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    )}
                    {apiKey?.value && canAccessSecret && (
                        <CopyToClipboard
                            text={apiKey.value}
                            onCopy={() => createNotification(translate({ id: 'CLIENT_COPIED_TO_CLIPBOARD' }), notificationType.SUCCESS)}>
                            <Tooltip title={translate({ id: 'CLIENT_COPY_API_KEY' })}>
                                <IconButton
                                    size="small"
                                    aria-label={translate({ id: 'CLIENT_COPY_API_KEY' })}
                                    data-cy={`consumables-v2-copy-key-${consumableCode.toLowerCase()}`}>
                                    <FileCopyOutlinedIcon
                                        color="primary"
                                        fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </CopyToClipboard>
                    )}
                    {canAccessSecret && consumableCodeToApiKeyIdentifier[consumableCode] && (
                        <Tooltip title={translate({ id: 'CLIENT_GENERATE_NEW' })}>
                            <IconButton
                                size="small"
                                aria-label={translate({ id: 'CLIENT_GENERATE_NEW' })}
                                onClick={() => regenerate(consumableCodeToApiKeyIdentifier[consumableCode])}
                                data-cy={`consumables-v2-regenerate-key-${consumableCode.toLowerCase()}`}>
                                <AutorenewIcon
                                    color="primary"
                                    fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            </div>
        </div>
    );
};
